import { create } from 'zustand';
import type { RecycleBinItem } from '../components/aiDetectionSections/RecycleBin';
import React from 'react';
// Generate dummy data for demo purposes
const generateDummyData = (): RecycleBinItem[] => {
  const now = new Date();
  
  return [];
};

/** Table keys from GET /recycle-bin response (e.g. face_enrollment, vehicle_enrollment, zones, detections, camera_ai_configs). */
export type RecycleBinTableTotals = Record<string, number>;

interface RecycleBinStore {
  items: RecycleBinItem[];
  /** Count from GET /recycle-bin face_enrollment; kept for backward compatibility */
  facesRecycleBinCount: number;
  /** Per-table totals from GET /recycle-bin (face_enrollment, vehicle_enrollment, zones, detections, camera_ai_configs). Used for tab badge and breakdown. */
  recycleBinTableTotals: RecycleBinTableTotals;
  setFacesRecycleBinCount: (count: number) => void;
  setRecycleBinTableTotals: (totals: RecycleBinTableTotals) => void;
  addItem: (item: Omit<RecycleBinItem, 'id' | 'deletedAt' | 'expiresAt'>) => void;
  restoreItem: (itemId: string) => RecycleBinItem | null;
  permanentlyDeleteItem: (itemId: string) => void;
  emptyBin: () => void;
  cleanupExpiredItems: () => void;
}

export const useRecycleBinStore = create<RecycleBinStore>((set, get) => ({
  items: generateDummyData(), // Initialize with dummy data
  facesRecycleBinCount: 0,
  recycleBinTableTotals: {},
  setFacesRecycleBinCount: (count) => set({ facesRecycleBinCount: count }),
  setRecycleBinTableTotals: (totals) => set({ recycleBinTableTotals: totals }),

  addItem: (item) => {
    const now = new Date();
    const expiresAt = new Date(now);
    expiresAt.setDate(expiresAt.getDate() + 60); // 60 days from now

    const newItem: RecycleBinItem = {
      ...item,
      id: `recycle-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      deletedAt: now,
      expiresAt,
    };

    set((state) => ({
      items: [...state.items, newItem],
    }));

    console.log('Item added to recycle bin:', newItem);
  },

  restoreItem: (itemId) => {
    const item = get().items.find((i) => i.id === itemId);
    if (!item) return null;

    set((state) => ({
      items: state.items.filter((i) => i.id !== itemId),
    }));

    console.log('Item restored from recycle bin:', item);
    return item;
  },

  permanentlyDeleteItem: (itemId) => {
    const item = get().items.find((i) => i.id === itemId);
    set((state) => ({
      items: state.items.filter((i) => i.id !== itemId),
    }));

    console.log('Item permanently deleted from recycle bin:', item);
  },

  emptyBin: () => {
    const count = get().items.length;
    set({ items: [] });
    console.log(`Recycle bin emptied: ${count} items permanently deleted`);
  },

  cleanupExpiredItems: () => {
    const now = new Date();
    const expiredItems = get().items.filter((item) => item.expiresAt <= now);
    
    if (expiredItems.length > 0) {
      set((state) => ({
        items: state.items.filter((item) => item.expiresAt > now),
      }));
      console.log(`Auto-cleanup: ${expiredItems.length} expired items removed from recycle bin`);
    }
  },
}));
